import Immutable from 'immutable';

// types
import {ActionTypes} from 'redux-undo';
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import ArtTypes from '~/constants/types/ArtTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import FundraisingTypes from '~/constants/types/FundraisingTypes';
import FontTypes from '~/constants/types/FontTypes';
import IconTypes from '~/constants/types/IconTypes';
import NotificationTypes from '~/constants/types/NotificationTypes';
import NamesAndNumbersTypes from '~/constants/types/NamesAndNumbersTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import TextTypes from '~/constants/types/TextTypes';
import UploadTypes from '~/constants/types/UploadTypes';
import * as NotificationConstants from '~/constants/NotificationConstants';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import {
  ADD_NEW_DESIGN_TO_CART_DIMENSION,
  ADD_NEW_DESIGN_TO_CART_QUERY_STRING,
  EDIT_DESIGN_FROM_CART_CONTENT_WARNING_DIMENSION,
  EDIT_DESIGN_FROM_CART_CONTENT_WARNING_QUERY_STRING,
  EDIT_DESIGN_FROM_CART_DIMENSION,
  EDIT_DESIGN_FROM_CART_QUERY_STRING
} from '~/constants/LabLoadMethods';
import {STATES, WCIBMOS} from '~/constants/QuoteConstants';
import remoteServices from '~/constants/RemoteServices';
import {ID_VIEW_MAP} from '~/constants/ProductConstants';
import {getRestrictedType} from '~/utils/search';

import {
  isArt,
  isClipartInverted,
  isNames,
  isNumbers,
  isText,
  isUpload
} from '~/data/accessors/icon';
import {getColorName, isNone, isWhite} from '~/data/accessors/color';
// utils
import {
  addComplementaryStylesPresent,
  buyNowPresent,
  getCartMode,
  getKeyword,
  getLabLoadMethod,
  keywordPresent,
  orderOptionsPresent
} from '~/utils/url';
import {viewConstraintChange} from '~/utils/view';
import {formatFontDetails, normalizeTagName} from '~/utils/analytics';
import {isInternal} from '~/utils/user';

import {metrics} from '~/vendor/metrics';

import {getSignalValueSelector} from '~/selectors/ConfigSelectors';
import {getTotalPrice} from '~/selectors/QuoteSelectors';
import {
  getAnchorDecorationMethod,
  getCurrentColorId,
  getRealTimePriceQuantity
} from '~/selectors/ProductSelectors';
import {isLabBlankLoad} from '~/selectors/CanvasSelectors';

const initialState = () => {
  return Immutable.Map({
    firedEvents: Immutable.Map(),
    initialBreakpoint: true,
    postalCodeChange: null,
    adPromoSource: 'load',
    productSummary: Immutable.Map(),
    externalEvents: Immutable.Map()
  });
};

/**
 * The analytics reducer deprecated, please migrate any new actions to
 * the metrics slices/metrics.
 */

export default function analytics(state = initialState(), action, fullState) {
  switch (action.type) {
    case ActionTypes.REDO:
      metrics.pageViewTag('ndx/contextualtools/redo');
      break;
    case ActionTypes.UNDO: {
      metrics.pageViewTag('ndx/contextualtools/undo');
      break;
    }
    case AnalyticsTypes.ADD_ART: {
      const {queryParams} = action.payload;

      if (!queryParams?.rs) break;

      const location = restingScreenCustomDimension(queryParams);

      metrics.setDimension('RestingScreenClick', location);
      metrics.pageViewTag('ndx/art/');
      return state.setIn(['firedEvents', action.type], true);
    }
    case AnalyticsTypes.ADD_COMPLEMENT:
      metrics.pageViewTag(`ndx/additems/${action.payload}/skirt`);
      break;
    case AnalyticsTypes.ADD_ITEMS:
      metrics.pageViewTag('ndx/additems');
      break;
    case AnalyticsTypes.CURRENT_PATH_PAGE_VIEW_TAG: {
      const {path} = action.payload;
      metrics.pageViewTag(`ndx${path}`);
      break;
    }
    case AnalyticsTypes.ADD_ITEMS_PICK_STYLE:
      metrics.setDimension('multiStyle', action.payload);
      metrics.pageViewTag(
        `ndx/additems/${action.meta.isCurrentProduct ? 'color' : 'style'}`
      );
      break;
    case AnalyticsTypes.ADD_ITEMS_CLOSE:
      metrics.pageViewTag('ndx/additems/close');
      break;
    case AnalyticsTypes.ADD_ITEMS_COLOR:
      metrics.pageViewTag('ndx/additems/color');
      break;
    case AnalyticsTypes.ADD_ITEMS_COLOR_CLOSE:
      metrics.pageViewTag('ndx/additems/color/close');
      break;
    case AnalyticsTypes.ADD_ITEMS_QUOTE:
      metrics.pageViewTag('ndx/quote/addstyle/added');
      break;
    case AnalyticsTypes.ADD_ITEMS_SELECT_COLOR:
      metrics.pageViewTag('ndx/additems/color/selected');
      break;
    case AnalyticsTypes.ADD_TEXT: {
      const {queryParams} = action.payload;
      const location = restingScreenCustomDimension(queryParams);
      metrics.setDimension('RestingScreenClick', location);
      metrics.pageViewTag('ndx/text');
      break;
    }
    case TextTypes.ADD_TEXT:
      metrics.pageViewTag('ndx/text/added');
      break;
    case AnalyticsTypes.ART_BROWSE: {
      const {categoryNamePath} = action.payload;
      metrics.pageViewTag(`ndx/art/browse/${categoryNamePath}`);
      break;
    }
    case AnalyticsTypes.ART_CLOSE:
      metrics.pageViewTag('ndx/art/close');
      break;
    case AnalyticsTypes.ART_PALETTE: {
      const {spotColor} = action.payload || {};

      metrics.pageViewTag('ndx/art/palette');

      const isTransparentByDefault =
        spotColor && isNone(spotColor.user) && isWhite(spotColor.original);

      if (isTransparentByDefault) {
        metrics.pageViewTag('ndx/art/colors/transparent');
      }
      break;
    }
    case AnalyticsTypes.ART_SWAP: {
      metrics.pageViewTag('ndx/art/swap_art');
      break;
    }
    case AnalyticsTypes.ART_CHANGE_COLOR: {
      const {color} = action.payload;
      if (isNone(color)) {
        metrics.pageViewTag('ndx/art/colors/changed/transparent');
      } else {
        metrics.pageViewTag(`ndx/art/colors/changed/${getColorName(color)}`);
      }
      break;
    }
    case AnalyticsTypes.ART_RESET_COLOR:
      metrics.pageViewTag('ndx/art/colors/reset');
      break;
    case AnalyticsTypes.ART_TOOLS: {
      metrics.pageViewTag('ndx/art/edit_page');
      break;
    }
    case AnalyticsTypes.COLOR_CTA_CLICK: {
      switch (action.payload) {
        case 'TEXT_OUTLINE':
          metrics.pageViewTag('ndx/text/outline/color/done');
          break;
        case 'TEXT':
          metrics.pageViewTag('ndx/text/palette/done');
          break;
        case 'CLIPART':
          metrics.pageViewTag('ndx/art/palette/done');
          break;
        case 'UPLOAD':
          metrics.pageViewTag('ndx/upload/editcolors/done');
          break;
      }
      break;
    }
    case AnalyticsTypes.CHAT:
      metrics.pageViewTag('ndx/chat');
      break;
    case AnalyticsTypes.DESIGN_TOOLS_OPEN:
      metrics.pageViewTag('ndx/mobile/designtools');
      break;
    case AnalyticsTypes.DESIGN_TOOLS_CLOSE:
      metrics.pageViewTag('ndx/mobile/designtools/close');
      break;
    case AnalyticsTypes.DESIGN_TOOLS_SELECT: {
      const {selectedOption} = action.payload;
      metrics.fireEvent('ndx mobile design tools screen', selectedOption);
      break;
    }
    case AnalyticsTypes.DISCONTINUED_PRODUCT:
      metrics.pageViewTag('ndx/product/discontinued');
      break;
    case AnalyticsTypes.DISCONTINUED_PRODUCT_COLOR:
      metrics.pageViewTag('ndx/product/color/discontinued');
      break;
    case AnalyticsTypes.ENTER_POSTAL_CODE: {
      const {mode} = action.payload;
      metrics.pageViewTag(`ndx/${mode}/zip`);
      break;
    }
    case AnalyticsTypes.FONT_BROWSE_SEE_MORE: {
      const {category} = action.payload;
      metrics.pageViewTag(
        `ndx/text/font/browse/seemore${normalizeTagName(category)}`
      );
      break;
    }
    case AnalyticsTypes.FUNDRAISING_MODE_WELCOME: {
      metrics.pageViewTag('ndx/fundraising/welcome');
      break;
    }
    case FundraisingTypes.SAVE_DESIGN: {
      metrics.pageViewTag('ndx/fundraising/save/confirm');
      break;
    }
    case AnalyticsTypes.FUNDRAISING_NAVIGATION: {
      metrics.pageViewTag('fundraising/createyourown/button');
      break;
    }
    case AnalyticsTypes.FUNDRAISING_CAMPAIGN_CLOSED: {
      metrics.pageViewTag('fundraising/createyourown/link');
      break;
    }
    case AnalyticsTypes.FUNDRAISING_MODE_GOALS: {
      metrics.pageViewTag('ndx/fundraising/goals');
      break;
    }
    case AnalyticsTypes.HIDE_CONTEXTUAL_TOOLS:
      metrics.pageViewTag('ndx/contextualtools/hide');
      break;
    case AnalyticsTypes.HELP_CALL:
      metrics.pageViewTag('ndx/help/call');
      break;
    case AnalyticsTypes.HELP_EMAIL:
      metrics.pageViewTag('ndx/help/email');
      break;
    case AnalyticsTypes.HELP_FEEDBACK:
      metrics.pageViewTag('ndx/help/feedback');
      break;
    case AnalyticsTypes.HELP_MENU: {
      const {queryParams} = action.payload;
      const location = restingScreenCustomDimension(queryParams);
      metrics.setDimension('RestingScreenClick', location);
      metrics.pageViewTag('ndx/help');
      break;
    }
    case AnalyticsTypes.HELP_VISIT_FULL_SITE:
      metrics.pageViewTag('ndx/help/visitFullSite');
      break;
    case DesignTypes.INVALID_UPLOAD:
      metrics.pageViewTag('ndx/error/brokenupload');
      break;
    case AnalyticsTypes.LOAD_WITHOUT_DESIGN: {
      metrics.pageTest('oex-alpinist-ndx-withoutdesign-06-06-2017');
      break;
    }
    case AnalyticsTypes.SLEEVE_PRINT_REMOVE_DESIGN: {
      if (!isInternal() && isLabBlankLoad(fullState)) {
        metrics.pageViewTag('/ndx/quote/pricetip/clicktoremovesleeve');
      }
      break;
    }
    case AnalyticsTypes.SLEEVE_PRINT_UNDO_REMOVE_DESIGN: {
      if (!isInternal() && isLabBlankLoad(fullState)) {
        metrics.pageViewTag(
          '/ndx/quote/pricetip/clicktoremovesleeve/reverttooriginal'
        );
      }
      break;
    }
    case AnalyticsTypes.SLEEVE_PRINT_NO_SLEEVE_IMAGE_SELECTED_PRODUCT: {
      const currentColorId = getCurrentColorId(fullState);

      if (currentColorId) {
        metrics.pageViewTag(
          `/ndx/additionalprintarea/sleeve/representativestyle/${currentColorId}`
        );
      }
      break;
    }
    case AnalyticsTypes.SLEEVE_ERROR_FUNDRAISING: {
      metrics.pageViewTag('/ndx/sleeve/error/fundraising');
      break;
    }
    case AnalyticsTypes.SLEEVE_ERROR_GOF: {
      metrics.pageViewTag('/ndx/sleeve/error/gof');
      break;
    }
    case AnalyticsTypes.SLEEVE_PRINT_ACTIONS: {
      const {sleeveAction, label} = action.payload;
      metrics.fireEvent('sleeve', sleeveAction, label);
      break;
    }
    case AnalyticsTypes.SLEEVE_PRINT_PRICE_INFO: {
      metrics.pageViewTag(action.payload);
      break;
    }
    case NotificationTypes.ERROR: {
      const {key, payload} = action.payload;
      let mismatchedAreas = [];
      let mismatchedNoPhotography = [];
      if (payload) {
        mismatchedAreas = payload.mismatchedAreas;
        mismatchedNoPhotography = payload.mismatchedNoPhotography;
      }
      switch (key) {
        case NotificationConstants.TOO_MANY_SLEEVE_COLORS: {
          metrics.pageViewTag('/ndx/sleeve/error/inkcolormax');
          break;
        }
        case NotificationConstants.SWAP_PRODUCT_MISMATCHED_AREAS: {
          if (mismatchedNoPhotography.length > 0) {
            if (
              mismatchedNoPhotography.includes('left sleeve') ||
              mismatchedNoPhotography.includes('right sleeve')
            ) {
              metrics.pageViewTag('/ndx/sleeve/error/notsupportedonline');
            }
          } else {
            if (
              mismatchedAreas.includes(VIEW_ID_MAP['leftSleeve']) ||
              mismatchedAreas.includes(VIEW_ID_MAP['rightSleeve'])
            ) {
              metrics.pageViewTag('/ndx/sleeve/error/incompatibleproduct');
            }
          }

          break;
        }
        case NotificationConstants.MULTI_STYLE_NAMES_AND_NUMBERS: {
          metrics.pageViewTag('/ndx/namesandnumbers/error/multiitem');
          break;
        }
      }
      break;
    }
    case AnalyticsTypes.ORDER_MORE_ADD_STYLES: {
      const {hasComplements} = action.payload;
      metrics.pageViewTag(
        hasComplements ? 'ndx/ordermore' : 'ndx/ordermore_nocomplements'
      );
      break;
    }
    case AnalyticsTypes.ORDER_MORE_ADD_ITEMS_ADD_STYLES: {
      const {hasComplements} = action.payload;
      metrics.pageViewTag(
        hasComplements
          ? 'ndx/additems/ordermore'
          : 'ndx/additems/ordermore_nocomplements'
      );
      break;
    }
    case AnalyticsTypes.ORDER_MORE_STYLES_ADDED: {
      const {isAddItems, isQuoteForm, styleName} = action.payload;

      if (styleName) {
        metrics.setDimension('orderAnythingProduct', styleName);
      }

      if (isAddItems) {
        metrics.pageViewTag('ndx/additems/ordermore/added');
      } else if (isQuoteForm) {
        metrics.pageViewTag('ndx/quote/ordermore/added');
      } else {
        metrics.pageViewTag('ndx/ordermore/added');
      }
      break;
    }
    case AnalyticsTypes.ORDER_MORE_QUOTE_ADD_STYLES: {
      const {hasComplements} = action.payload;
      metrics.pageViewTag(
        hasComplements
          ? 'ndx/quote/ordermore'
          : 'ndx/quote/ordermore_nocomplements'
      );
      break;
    }
    case AnalyticsTypes.MAIN_MENU_OPEN:
      metrics.pageViewTag('ndx/menu');
      break;
    case AnalyticsTypes.ADD_DESIGN:
      metrics.pageViewTag('ndx/adddesign');
      break;
    case AnalyticsTypes.MAIN_MENU_NAVIGATE:
      metrics.fireEvent('ndx meganav', action.payload);
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS:
      metrics.pageViewTag('ndx/namesandnumbers');
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS_CLOSE:
      metrics.pageViewTag('ndx/namesandnumbers/close');
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS_COLORS: {
      const {type} = action.payload;
      metrics.pageViewTag(
        `ndx/namesandnumbers/tools/${type?.toLowerCase()}/colors`
      );
      break;
    }
    case AnalyticsTypes.NAMES_AND_NUMBERS_GET_STARTED:
      metrics.pageViewTag('ndx/namesandnumbers/getstarted');
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS_POSITION: {
      const {type, viewId} = action.payload;
      metrics.pageViewTag(
        `ndx/namesandnumbers/tools/${type.toLowerCase()}/position/${
          ID_VIEW_MAP[viewId]
        }`
      );
      break;
    }
    case AnalyticsTypes.NAMES_AND_NUMBERS_REDIRECT:
      metrics.pageViewTag('ndx/namesandnumbers');
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS_LIST:
      metrics.pageViewTag('ndx/namesandnumbers/list');
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS_ALERT:
      metrics.pageViewTag('ndx/namesandnumbers/list/alert/' + action.payload);
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS_COMPLETE:
      metrics.pageViewTag('ndx/namesandnumbers/list/complete');
      break;
    case AnalyticsTypes.NAMES_AND_NUMBERS_EDIT:
      metrics.pageViewTag('ndx/namesandnumbers/tools/edit');
      break;
    case AnalyticsTypes.NEXT: {
      const {lastPath, hasQuote} = action.payload;
      if (lastPath && lastPath.toLowerCase() === '/saveconfirmation/') {
        metrics.pageViewTag('ndx/save/getPrice');
      } else {
        const hasQuoteTag = hasQuote ? 'withquote' : 'withoutquote';
        metrics.pageViewTag('ndx/next');
        metrics.pageViewTag(`ndx/next/${hasQuoteTag}`);
      }
      break;
    }
    case AnalyticsTypes.NEXT_GOF:
      metrics.pageViewTag('ndx/next/group_order');
      break;
    case AnalyticsTypes.NEXT_FUNDRAISING:
      metrics.pageViewTag('ndx/next/fundraising');
      break;
    case AnalyticsTypes.NDX_START: {
      // As this is the initial viewport, using window.inner(Width/Height) is ok
      metrics.setDimension(
        'ViewPort',
        `${window.innerWidth}x${window.innerHeight}`
      );

      const {categoryName, colorId, designIdeaId, productColor, withDesign} =
        action.payload;

      metrics.setDimension('labLoadProductStyle', colorId);
      metrics.setDimension('labLoadProductColor', productColor);
      metrics.setDimension('WithDesign', withDesign);
      metrics.setDimension('labLoadProductCategory', categoryName);
      metrics.setDimension('labLoadDesignTemplateID', designIdeaId);

      if (labLoadMethodCustomDimension()) {
        metrics.setDimension('labLoadMethod', labLoadMethodCustomDimension());
      }

      metrics.pageViewTag('ndx/start');
      // url parameter handling
      if (orderOptionsPresent()) {
        metrics.setDimension('labLoadOrderOptions', true);
      } else if (buyNowPresent()) {
        metrics.setDimension('labLoadBuyNow', true);
      } else if (addComplementaryStylesPresent()) {
        metrics.setDimension('labLoadComplementaryStyles', true);
      } else if (keywordPresent()) {
        metrics.setDimension('labLoadKeyword', getKeyword());
      }

      metrics.fireEvent(
        'print method',
        'lab load',
        `${getAnchorDecorationMethod(fullState)}`
      );

      break;
    }
    case AnalyticsTypes.NDX_BREAKPOINT: {
      const breakpoint = action.payload;
      metrics.setDimension('Breakpoint', breakpoint);
      if (state.get('initialBreakpoint')) {
        state = state.set('initialBreakpoint', false);
      } else {
        metrics.pageViewTag('ndx/breakpoint/change');
      }
      break;
    }
    case AnalyticsTypes.NOTES: {
      metrics.pageViewTag('ndx/notes');
      break;
    }
    case AnalyticsTypes.PRINT_THIS: {
      metrics.pageViewTag('print/proof');
      break;
    }

    case AnalyticsTypes.PRODUCT_CATALOG: {
      const {queryParams} = action.payload;
      const location = restingScreenCustomDimension(queryParams);
      metrics.setDimension('RestingScreenClick', location);
      if (action.payload === 'close') {
        metrics.pageViewTag('ndx/product/close');
      }
      break;
    }
    case AnalyticsTypes.PRODUCT_COLOR:
      metrics.pageViewTag(`ndx/productcolor/${action.payload.prompt}`);
      break;
    case AnalyticsTypes.PRODUCT_COLOR_NOT_CHANGED:
      metrics.pageViewTag('ndx/productcolor/close');
      break;
    case AnalyticsTypes.PRODUCT_COLOR_CHANGED:
      metrics.pageViewTag(`ndx/catalog/productcolor/changed/${action.payload}`);
      break;
    case AnalyticsTypes.PRODUCT_COLOR_TAB_CHANGED:
      metrics.pageViewTag(`ndx/catalog/productcolor/${action.payload}`);
      break;
    case AnalyticsTypes.PRODUCT_COLOR_TOGGLE_CHANGED:
      metrics.pageViewTag(
        `ndx/catalog/productcolor/${action.payload.quantityFilter}`
      );
      break;
    case AnalyticsTypes.PRODUCT_DETAILS: {
      const tag =
        action.payload === 'close'
          ? 'ndx/product/close'
          : 'ndx/product/details';
      metrics.pageViewTag(tag);
      break;
    }
    case AnalyticsTypes.PRODUCT_PRINT_AREA: {
      metrics.sessionPageViewTag('/ndx/product/printarea/alert');
      break;
    }
    case AnalyticsTypes.PRODUCT_SUMMARY: {
      const {section, event} = action.payload;
      if (!state.getIn(['productSummary', section, event])) {
        metrics.pageViewTag(`ndx/${section}/${event}`);
        state = state.setIn(['productSummary', section, event], true);
      }
      break;
    }
    case AnalyticsTypes.PRODUCT_SUMMARY_CAROUSEL: {
      const {styleId, imageOrdinal} = action.payload;
      metrics.fireEvent({
        action: styleId,
        category: 'ndx product catalog',
        label: imageOrdinal
      });
      break;
    }
    case AnalyticsTypes.PRODUCT_SUMMARY_TAG:
      metrics.pageViewTag('ndx/catalog/tray');
      break;
    case AnalyticsTypes.PRODUCT_WELCOME:
      metrics.pageViewTag('ndx/catalog/welcome');
      break;
    case AnalyticsTypes.BUY_FORM:
      metrics.pageViewTag('ndx/buy/form');
      break;
    case AnalyticsTypes.QUOTE_BLANK_DESIGN:
      metrics.pageViewTag('ndx/quote/error/blankdesign');
      break;
    case AnalyticsTypes.QUOTE_FORM:
      metrics.pageViewTag('ndx/quote/form');
      break;
    case AnalyticsTypes.QUOTE_FORM_POSTAL_CODE_CHANGE: {
      const {currentPostalCode, newPostalCode} = action.payload;
      metrics.setDimension(
        'postalCodeChange',
        `${currentPostalCode}_${newPostalCode}`
      );
      metrics.pageViewTag('ndx/quote/zip/change');
      if (currentPostalCode && newPostalCode) {
        return state.set(
          'postalCodeChange',
          `${currentPostalCode}_${newPostalCode}`
        );
      } else {
        break;
      }
    }
    case AnalyticsTypes.QUOTE_STATE_CHANGE: {
      const {
        anchorProductCategory,
        anchorProductColor,
        anchorProductStyleNumber,
        caveat,
        isSpecialty,
        mode,
        newState,
        oldState,
        overColorLimit,
        postalCode,
        sellBlankAllowed,
        showBlankDesignMessage,
        totalPrice,
        totalQuantity
      } = action.payload;
      switch (newState) {
        case STATES.QUOTE_FORM: {
          if (oldState === STATES.MULTI_ITEM_SUMMARY) {
            metrics.pageViewTag(`ndx/${mode}/reprice`);
          } else {
            metrics.pageViewTag(`ndx/${mode}/form`);
            break;
          }
          break;
        }
        case STATES.SHIPPING_FORM: {
          if (oldState === STATES.MULTI_ITEM_SUMMARY) {
            metrics.pageViewTag(`ndx/${mode}/zip/update`);
          } else {
            metrics.pageViewTag(`ndx/${mode}/zip`);
          }
          break;
        }
        case STATES.MULTI_ITEM_SUMMARY: {
          // oldState === newState is a hack for handling special blank design case
          // otherwise this handles all bulk and singles tags
          // google tag manager
          metrics.setDimension('caveat', caveat);
          metrics.setDimension('quotedProductStyle', anchorProductStyleNumber);
          metrics.setDimension('quoteProductColor', anchorProductColor);
          metrics.setDimension('postalCode', postalCode);
          metrics.setDimension('quantity', totalQuantity);
          metrics.setDimension('quotedPrice', totalPrice);
          if (totalQuantity < 6) {
            metrics.pageViewTag(`ndx/${mode}/price/singles`);
          } else {
            metrics.pageViewTag(`ndx/${mode}/price/bulk`);
          }
          if (showBlankDesignMessage) {
            metrics.pageViewTag(`ndx/${mode}/blank`);
          }
          break;
        }
        case STATES.MONEY_SAVING_TIPS: {
          if (isSpecialty) {
            metrics.pageViewTag(`ndx/${mode}/moneytips/specialty`);
          } else if (totalQuantity > 5) {
            metrics.pageViewTag(`ndx/${mode}/moneytips`);
          } else if (totalQuantity < 6) {
            metrics.pageViewTag(`ndx/${mode}/moneytips/singles`);
          }
          break;
        }
        case STATES.IMPOSSIBLE_TO_QUOTE: {
          if (overColorLimit) {
            metrics.pageViewTag(`ndx/${mode}/error/colorlimit`);
            metrics.setDimension(
              'quotedProductStyle',
              anchorProductStyleNumber
            );
            metrics.setDimension('quoteProductColor', anchorProductColor);
            metrics.setDimension('quoteProductCategory', anchorProductCategory);
          }
          if (sellBlankAllowed) {
            metrics.pageViewTag(`ndx/${mode}/error/sellblank`);
          }
          break;
        }
      }
      break;
    }
    case AnalyticsTypes.QUOTE_WCIBMO: {
      const {
        anchorProductCategory,
        anchorProductColor,
        anchorProductStyleNumber,
        backColors,
        frontColors,
        mode,
        multiStyle,
        postalCode,
        totalQuantity,
        wcibmo
      } = action.payload;
      const belowmin = () => {
        metrics.setDimension('quotedProductStyle', anchorProductStyleNumber);
        metrics.setDimension('quantity', totalQuantity);
        metrics.setDimension('quotedProductColor', anchorProductColor);
        metrics.setDimension('quotedProductCategory', anchorProductCategory);
        metrics.setDimension('multiStyle', multiStyle);
      };
      const belowminHighColor = () => {
        belowmin();
        if (frontColors) {
          metrics.setDimension('colorCountFront', frontColors);
        }
        if (backColors) {
          metrics.setDimension('colorCountBack', backColors);
        }
      };
      switch (wcibmo) {
        case WCIBMOS.INVALID_ZIP: {
          if (postalCode) {
            metrics.pageViewTag(`ndx/${mode}/error/badzip`);
            metrics.setDimension('postalCode', postalCode);
          } else {
            metrics.pageViewTag(`ndx/${mode}/error/nozip`);
          }
          break;
        }
        case WCIBMOS.ENTER_QUANTITIES:
          metrics.pageViewTag(`ndx/${mode}/error/noquantity`);
          break;
        case WCIBMOS.QUOTE_SERVICE_ERROR:
          metrics.pageViewTag(`ndx/${mode}/error/quote_service_error`);
          break;
        case WCIBMOS.CANADA_TO_US:
          metrics.setDimension('quotedProductStyle', anchorProductStyleNumber);
          metrics.pageViewTag(`ndx/${mode}/error/canada_products_to_us`);
          break;
        case WCIBMOS.US_TO_CANADA:
          metrics.setDimension('quotedProductStyle', anchorProductStyleNumber);
          metrics.pageViewTag(`ndx/${mode}/error/us_products_to_canada`);
          break;
        case WCIBMOS.RAISE_QTY:
          belowmin();
          metrics.pageViewTag(`ndx/${mode}/error/belowmin`);
          break;
        case WCIBMOS.RAISE_QTY_CHANGE_PRODUCT:
          belowmin();
          metrics.pageViewTag(`ndx/${mode}/error/belowmin/color`);
          break;
        case WCIBMOS.RAISE_QTY_CHANGE_STYLE:
          metrics.pageViewTag(`ndx/${mode}/error/belowmin`);
          belowmin();
          break;
        case WCIBMOS.RAISE_QTY_CHANGE_TO_ALTERNATE:
          belowmin();
          metrics.pageViewTag(`ndx/${mode}/error/belowmin/alternate`);
          break;
        case WCIBMOS.RAISE_QTY_ABOVE_HI_COLOR_MIN:
          belowminHighColor();
          metrics.pageViewTag(`ndx/${mode}/error/belowmin/highcolor`);
          break;
        case WCIBMOS.RAISE_QTY_ABOVE_HI_CHANGE_PRODUCT:
          belowminHighColor();
          metrics.pageViewTag(
            `ndx/${mode}/error/belowmin/highcolor/alt_product`
          );
          break;
        case WCIBMOS.RAISE_QTY_ABOVE_HI_CHANGE_STYLE:
          belowminHighColor();
          metrics.pageViewTag(`ndx/${mode}/error/belowmin/highcolor/alt_style`);
          break;
        case WCIBMOS.RAISE_QTY_ABOVE_HI_CHANGE_TO_ALTERNATE:
          belowminHighColor();
          metrics.pageViewTag(`ndx/${mode}/error/belowmin/highcolor/alternate`);
          break;
        case WCIBMOS.RAISE_QTY_REMOVE_SLEEVE:
          metrics.pageViewTag('/ndx/sleeve/error/singles');
          break;
        case WCIBMOS.SIZES_NOT_AVAILABLE:
          metrics.pageViewTag(`ndx/${mode}/error/sizes_not_available`);
          break;
      }
      break;
    }
    case AnalyticsTypes.SELECT_FONT:
      metrics.pageViewTag('ndx/text/font');
      break;
    case AnalyticsTypes.SAVE_FORM: {
      const {path, queryParams} = action.payload;
      const location = restingScreenCustomDimension(queryParams);
      if (location && !queryParams.referrer) {
        metrics.setDimension('RestingScreenClick', location);
      }
      const p = path.toLowerCase();

      switch (p) {
        case '/namesandnumbers/saveform':
        case '/namesandnumbers/saveform/':
          metrics.pageViewTag('ndx/namesandnumbers/save/form');
          break;
        case '/notes/saveform':
        case '/notes/saveform/':
          metrics.pageViewTag('ndx/notes/save/form');
          break;
        case (queryParams.referrer === 'help' ||
          queryParams.referrer === 'help/') &&
          p:
          metrics.pageViewTag('ndx/help/save/form');
          break;
        case (queryParams.referrer === 'art' ||
          queryParams.referrer === 'art/') &&
          p:
          metrics.pageViewTag('ndx/art/save/form');
          break;
        case (queryParams.referrer === 'upload' ||
          queryParams.referrer === 'upload/') &&
          p:
          metrics.pageViewTag('ndx/upload/save/form');
          break;
        default:
          metrics.pageViewTag('ndx/save/form');
          break;
      }

      break;
    }
    case AnalyticsTypes.NOTES_ADDED: {
      metrics.pageViewTag('ndx/notes/added');
      break;
    }
    case AnalyticsTypes.REAL_TIME_PRICE_CHANGE: {
      const totalPrice = getTotalPrice(fullState);
      const totalQuantity = getRealTimePriceQuantity(fullState);
      metrics.setDimension(
        'realTimeItemPrice',
        `${totalPrice}_${totalQuantity}`
      );
      break;
    }
    case AnalyticsTypes.REAL_TIME_PRICE_QUANTITY_CHANGE: {
      metrics.pageViewTag('ndx/quote/rtp');
      break;
    }
    case AnalyticsTypes.REAL_TIME_PRICING_TEST: {
      const flag = getSignalValueSelector('realTimePricingV14')(fullState);
      const eventFired = state.getIn(['firedEvents', action.type]);
      const date = '20';
      if (!eventFired) {
        metrics.fireEvent(
          'testing',
          `helios rtp feb ${date} 20`,
          `helios rtp ${flag} feb ${date} 20`
        );
        metrics.pageTest(`rtp-${flag}-02${date}20`);
      }
      return state.setIn(['firedEvents', action.type], true);
    }
    case AnalyticsTypes.SHARE_FACEBOOK: {
      metrics.pageViewTag('ndx/share/facebook');
      break;
    }
    case AnalyticsTypes.SHARE_LINK: {
      metrics.pageViewTag('ndx/share/email/link');
      break;
    }
    case AnalyticsTypes.SHARE_EMAIL: {
      metrics.pageViewTag('ndx/share/email/form');
      break;
    }
    case AnalyticsTypes.SHARE_PINTEREST: {
      metrics.pageViewTag('ndx/share/pinterest');
      break;
    }
    case AnalyticsTypes.SHARE_TWITTER: {
      metrics.pageViewTag('ndx/share/twitter');
      break;
    }
    case AnalyticsTypes.TEST_INTO_DECO_FLEX: {
      const eventFired = state.getIn(['firedEvents', action.type]);
      if (!eventFired) {
        // The Artemis Deco Flex Test, decoFlex_v3, was temporarily turned off.
        // It will be turned back on in the future after a reevaluation.
      }
      return state.setIn(['firedEvents', action.type], true);
    }
    case AnalyticsTypes.TEXT_EFFECT_DONE:
      metrics.pageViewTag('ndx/text/shape/done');
      break;
    case AnalyticsTypes.TEXT_EFFECT_INTENSITY:
      metrics.sessionPageViewTag('ndx/text/effect/intensity');
      break;
    case TextTypes.UPDATE_EFFECT: {
      const {effectName} = action.payload;
      metrics.pageViewTag(`ndx/text/effect/${effectName}`);
      break;
    }
    case AnalyticsTypes.TEXT_OUTLINE_ON:
      metrics.sessionPageViewTag('ndx/text/outline/on');
      break;
    case AnalyticsTypes.TEXT_OUTLINE_INTENSITY:
      metrics.sessionPageViewTag('ndx/text/outline/intensity');
      break;
    case AnalyticsTypes.TEXT_OUTLINE_COLOR:
      metrics.pageViewTag('ndx/text/outline/color');
      break;
    case AnalyticsTypes.TEXT_OUTLINE_OFF:
      metrics.pageViewTag('ndx/text/outline/off');
      break;
    case AnalyticsTypes.TEXT_TOOLS: {
      metrics.pageViewTag('ndx/text/edit');
      break;
    }
    case AnalyticsTypes.UPDATE_EXTERNAL_EVENT: {
      const payload = action.payload || {};
      const keyName = Object.keys(payload)[0];
      if (keyName) {
        return state.setIn(
          ['externalEvents', keyName],
          action.payload[keyName]
        );
      }
      break;
    }
    case AnalyticsTypes.UPLOAD_CLOSE_TOOLS:
      metrics.pageViewTag('ndx/upload/close/tools');
      break;
    case AnalyticsTypes.UPLOAD_CHANGE_COLOR:
      metrics.pageViewTag('ndx/upload/editcolors/adjust');
      break;
    case UploadTypes.UPLOAD_FAILED: {
      metrics.pageViewTag('ndx/upload/failure');
      break;
    }
    case AnalyticsTypes.UPLOAD_RESET_COLOR:
      metrics.pageViewTag('ndx/upload/editcolors/reset');
      break;
    case AnalyticsTypes.UPLOAD_TOGGLE_BACKGROUND: {
      const {visible} = action.payload;
      metrics.pageViewTag(`ndx/upload/background/${visible ? 'on' : 'off'}`);
      break;
    }
    case AnalyticsTypes.UPLOAD_TOGGLE_PRODUCT_COLOR: {
      const {visible} = action.payload;
      metrics.pageViewTag(`ndx/upload/hidecolor/${visible ? 'on' : 'off'}`);
      break;
    }
    case AnalyticsTypes.UPLOAD_OR_BROWSE_ART: {
      const {label} = action.payload;
      metrics.fireEvent('your photo here', 'assisted design', label);
      break;
    }
    case AnalyticsTypes.VIEW_ALL_FONT_CATEGORIES:
      metrics.pageViewTag('ndx/text/viewallfontcategories');
      break;
    case AnalyticsTypes.WELCOME:
      metrics.pageViewTag('ndx/welcome');
      break;
    case AnalyticsTypes.WELCOME_BACK:
      metrics.pageViewTag('ndx/welcomeback');
      break;
    case AnalyticsTypes.WELCOME_BACK_CONTINUE:
      metrics.pageViewTag('ndx/welcomeback/continue');
      break;
    case AnalyticsTypes.WELCOME_BACK_NEW:
      metrics.pageViewTag('ndx/welcomeback/new');
      break;
    case AnalyticsTypes.ZOOM_IN:
      metrics.pageViewTag('ndx/zoom/in');
      break;
    case AnalyticsTypes.ZOOM_OUT: {
      metrics.pageViewTag('ndx/zoom/out');
      break;
    }
    case ArtTypes.ADD_ART: {
      const {icon} = action.payload;

      metrics.setDimension('clipartid', icon.clipartId);
      metrics.setDimension('artMultiColor', !icon.blackwhite);
      metrics.pageViewTag('ndx/art/added');

      if (isClipartInverted(icon)) {
        metrics.pageViewTag('ndx/art/added/inverted');
      }
      break;
    }
    case IconTypes.UPDATE_HORIZONTAL_FLIP: {
      const {iconType} = action.meta;
      if (isUpload(iconType)) {
        metrics.pageViewTag('ndx/upload/flip_h');
      } else {
        metrics.pageViewTag('ndx/art/flip_h');
      }
      break;
    }
    case ArtTypes.UPDATE_LOCK_RATIO:
      metrics.pageViewTag('ndx/art/lock_ratio');
      break;
    case IconTypes.UPDATE_SINGLE_COLOR: {
      const {singleColor} = action.payload;
      const {iconType} = action.meta;
      const onOff = singleColor ? 'on' : 'off';

      if (isUpload(iconType)) {
        metrics.pageViewTag(`ndx/upload/onecolor/${onOff}`);
      } else {
        metrics.pageViewTag(`ndx/art/onecolor/${onOff}`);
      }
      break;
    }
    case ArtTypes.UPDATE_TRANSPARENCY: {
      const {transparency} = action.payload;
      metrics.pageViewTag(`ndx/art/showwhite/${transparency ? 'off' : 'on'}`);
      break;
    }
    case IconTypes.UPDATE_VERTICAL_FLIP: {
      const {iconType} = action.meta;
      if (isUpload(iconType)) {
        metrics.pageViewTag('ndx/upload/flip_v');
      } else {
        metrics.pageViewTag('ndx/art/flip_v');
      }
      break;
    }
    case ArtTypes.SEARCH_LOADED: {
      const {category, resource, fireAnalytics} = action.payload;
      if (fireAnalytics) {
        const results = resource.count > 0 ? 'results' : 'noresults';
        let restrictedType = getRestrictedType(category);
        restrictedType = restrictedType ? `${restrictedType}/` : '';
        const tag = `ndx/art/${results}/search/${restrictedType}${normalizeTagName(
          category
        )}`;
        metrics.pageViewTag(tag);
      }
      break;
    }
    case IconTypes.ALIGN: {
      const {align} = action.payload;
      metrics.pageViewTag(`ndx/align/${align}`);
      break;
    }
    case IconTypes.BACKWARD:
    case IconTypes.CENTER:
    case IconTypes.COPY:
    case IconTypes.CUT:
    case IconTypes.FORWARD:
    case IconTypes.PASTE: {
      const type = action.type.replace('icon/', '').toLowerCase();
      metrics.pageViewTag(`ndx/contextualtools/${type}`);
      break;
    }
    case IconTypes.CHANGE_COLOR: {
      const {type} = action.payload;
      if (isNames(type)) {
        metrics.pageViewTag('ndx/namesandnumbers/tools/names/colors/selected');
      } else if (isNumbers(type)) {
        metrics.pageViewTag(
          'ndx/namesandnumbers/tools/numbers/colors/selected'
        );
      }
      break;
    }
    case IconTypes.MOVE: {
      const {transforms} = action.payload;

      transforms.forEach(({icon}) => {
        if (isArt(icon.type)) {
          metrics.pageViewTag('ndx/art/move');
        } else if (isText(icon.type)) {
          metrics.pageViewTag('ndx/text/move');
        } else if (isUpload(icon.type)) {
          metrics.pageViewTag('ndx/upload/move');
        }
      });

      break;
    }
    case IconTypes.REMOVE: {
      const {iconTypes, source} = action.payload;

      if (source === 'contextmenu') {
        metrics.pageViewTag('ndx/contextualtools/delete');
      }

      iconTypes.forEach(iconType => {
        if (isArt(iconType)) {
          metrics.pageViewTag('ndx/art/deleted');
        } else if (isText(iconType)) {
          metrics.pageViewTag('ndx/text/deleted');
        } else if (isNames(iconType)) {
          metrics.pageViewTag('ndx/namesandnumbers/tools/names/off');
        } else if (isNumbers(iconType)) {
          metrics.pageViewTag('ndx/namesandnumbers/tools/numbers/off');
        } else if (isUpload(iconType)) {
          metrics.pageViewTag('ndx/upload/deleted');
        }
      });

      break;
    }
    case IconTypes.RESIZE: {
      const {transforms, source} = action.payload;

      if (transforms.length > 1) {
        metrics.pageViewTag('ndx/multiselect/resize');
      }

      transforms.forEach(({icon}) => {
        if (isArt(icon.type)) {
          metrics.pageViewTag(`ndx/art/resize/${source}`);
        } else if (isText(icon.type)) {
          metrics.pageViewTag(`ndx/text/resize/${source}`);
        } else if (isUpload(icon.type)) {
          metrics.pageViewTag(`ndx/upload/resize/${source}`);
        }
      });

      break;
    }
    case IconTypes.TOGGLE_INVERTED_CLIPART: {
      const {isInverted} = action.payload;
      metrics.pageViewTag(`/ndx/art/invert/${isInverted ? 'on' : 'off'}`);
      break;
    }
    case CanvasTypes.CHANGE_VIEW: {
      const viewId = action.payload;
      metrics.pageViewTag(`ndx/rotate/${ID_VIEW_MAP[viewId]}`);
      break;
    }

    case DesignTypes.CLEAR_DESIGN_DATA: {
      // for now we only want to clear the Real Time pricing fired events
      return state.setIn(
        ['firedEvents', 'analytics/REAL_TIME_PRICING_TEST'],
        false
      );
    }
    case DesignTypes.SAVE_SUCCESS: {
      const {
        categoryName,
        colorId,
        firstTimeSaving,
        hasArtIcon,
        productColor,
        response,
        savedDesignID
      } = action.payload;

      metrics.setDimension('firstTimeSaver', !!firstTimeSaving);
      metrics.setDimension('saveDesignID', savedDesignID);
      metrics.setDimension('saveProductStyle', colorId);
      metrics.setDimension('saveProductCategory', categoryName);
      metrics.setDimension('saveProductColor', productColor);
      metrics.setDimension('saveWithArt', hasArtIcon.toString());

      const userId = response.design && response.design.account_token;
      if (userId) {
        metrics.setDimension('userId', userId);
        metrics.registerUser(userId);
      }
      metrics.pageViewTag('ndx/save/confirm');

      if (window.parent && window.parent.postMessage) {
        const {composite_id} = response.design;
        window.parent.postMessage(
          {composite_id},
          remoteServices.fundraising_origin
        );
      }

      metrics.fireEvent(
        'print method',
        'save',
        `${getAnchorDecorationMethod(fullState)}`
      );

      break;
    }
    case DesignTypes.SAVE_FAILED: {
      metrics.pageViewTag('ndx/save/error');
      break;
    }
    case DesignTypes.SHARE_ERROR:
      metrics.pageViewTag('ndx/share/email/error');
      break;
    case DesignTypes.SHARE_FAILURE:
      metrics.pageViewTag('ndx/share/email/error');
      break;
    case DesignTypes.SHARE_SUCCESS:
      metrics.pageViewTag('ndx/share/email/confirm');
      break;
    case FontTypes.CATEGORY_LOADED: {
      const {category} = action.payload;
      metrics.pageViewTag(`ndx/text/font/browse/${normalizeTagName(category)}`);
      break;
    }
    case ProductTypes.ADD_PRODUCT: {
      const {metricsEnabled, newStyle} = action.meta;
      if (metricsEnabled) {
        metrics.setDimension(
          'multiStyle',
          action.payload.products.map(p => p.colorId).join(',')
        );
        metrics.pageViewTag(
          `ndx/additems/${newStyle ? 'style' : 'color'}/selected`
        );
      }
      break;
    }
    case ProductTypes.CHANGE_COLOR: {
      const {fireAnalytics} = action.meta;
      if (!fireAnalytics) break;
      metrics.pageViewTag('ndx/productcolor/changed');
      break;
    }
    case ProductTypes.DELETE_PRODUCT: {
      if (!action.meta?.fireAnalytics) break;
      metrics.pageViewTag('ndx/additems/remove');
      break;
    }
    case ProductTypes.SWAP_PRODUCT: {
      const {
        changedProductCategory,
        decorationMethod,
        isInitialLabLoad,
        oldViews,
        views
      } = action.payload;
      const {isAD, fireAnalytics} = action.meta;

      if (!fireAnalytics) break;

      if (!isAD) {
        if (viewConstraintChange(oldViews, views, 0.5)) {
          metrics.pageViewTag('ndx/constraints/resize');
        }
        if (!isInitialLabLoad) {
          metrics.setDimension(
            'changedProductCategory',
            changedProductCategory
          );
          metrics.pageViewTag('ndx/product/changed');
        }
      }

      metrics.fireEvent('print method', 'change product', decorationMethod);

      break;
    }
    case AnalyticsTypes.SWAP_PRODUCT_WARNING: {
      let pvTag = '/ndx/style/compatible/error';
      if (action.payload === 'continue') {
        pvTag = `${pvTag}/continue`;
      }
      metrics.pageViewTag(pvTag);
      break;
    }
    case IconTypes.ROTATE: {
      const {iconType} = action.meta;

      if (isUpload(iconType)) {
        metrics.pageViewTag('ndx/upload/rotate');
      } else if (isText(iconType)) {
        metrics.pageViewTag('ndx/text/rotate');
      } else {
        metrics.pageViewTag('ndx/art/rotate');
      }

      return state.setIn(['firedEvents', action.type], true);
    }
    case TextTypes.UPDATE_FONT_HEIGHT: {
      const {iconType} = action.payload;
      if (isNames(iconType)) {
        metrics.pageViewTag('ndx/namesandnumbers/tools/names/height/edit');
      } else if (isNumbers(iconType)) {
        metrics.pageViewTag('ndx/namesandnumbers/tools/numbers/height/edit');
      } else {
        metrics.pageViewTag('ndx/text/resize/input');
      }
      break;
    }
    case TextTypes.UPDATE_FONT: {
      const {fontCategory, fontName} = action.payload;
      metrics.pageViewTag(
        `ndx/text/font/select/${formatFontDetails({fontCategory, fontName})}`
      );
      break;
    }
    case TextTypes.UPDATE_OUTLINE: {
      const {outlineWidth} = action.payload;
      const status = outlineWidth === 0 ? 'off' : 'on';
      metrics.pageViewTag(`ndx/text/outline/${status}`);
      break;
    }
    case TextTypes.UPDATE_TEXT:
      metrics.pageViewTag('ndx/text/update');
      break;
    case NamesAndNumbersTypes.CREATE:
      metrics.pageViewTag('ndx/namesandnumbers/list/add');
      break;
    case NamesAndNumbersTypes.ADD_NAMES:
      metrics.pageViewTag('ndx/namesandnumbers/tools/names/on');
      break;
    case NamesAndNumbersTypes.ADD_NUMBERS:
      metrics.pageViewTag('ndx/namesandnumbers/tools/numbers/on');
      break;
    case NamesAndNumbersTypes.REMOVE:
      metrics.pageViewTag('ndx/namesandnumbers/tools/names/on');
      break;
    case UploadTypes.ADD_SPOT_COLOR:
      metrics.pageViewTag('ndx/upload/spotColors/add');
      break;
    case UploadTypes.REMOVE_SPOT_COLOR:
      metrics.pageViewTag('ndx/upload/spotColors/remove');
      break;
    case UploadTypes.RESET_UPLOAD:
      metrics.pageViewTag('ndx/upload/reset/original');
      break;
    case UploadTypes.UPLOAD_IN_PROGRESS:
      metrics.pageViewTag('ndx/upload/progress');
      break;
    case AnalyticsTypes.UPLOAD_SUCCESS: {
      const {colorMode, colorCount, gamutColorCount, extension} =
        action.payload;
      if (colorCount) {
        metrics.setDimension('uploadColorCount', colorCount);
      }
      if (gamutColorCount) {
        metrics.setDimension('uploadGamutColorCount', gamutColorCount);
      }
      metrics.setDimension('uploadColorType', colorMode);
      metrics.pageViewTag(`ndx/upload/success/image/${extension}`);
      break;
    }
    default:
      break;
  }
  return state;
}

export const restingScreenCustomDimension = queryParams => {
  if (!queryParams) return;
  switch (queryParams.rs) {
    case 'wb':
    case 'wb/':
      return 'welcomeback';
    case 'rs':
    case 'rs/':
      return 'restingscreen';
    case 'w':
    case 'w/':
      return 'welcome';
    case 'm':
    case 'm/':
      return 'menu';
    default:
      return 'toolbar';
  }
};

export const labLoadMethodCustomDimension = () => {
  switch (getLabLoadMethod()) {
    case ADD_NEW_DESIGN_TO_CART_QUERY_STRING:
      if (getCartMode()) {
        return ADD_NEW_DESIGN_TO_CART_DIMENSION;
      } else {
        return;
      }
    case EDIT_DESIGN_FROM_CART_QUERY_STRING:
      return EDIT_DESIGN_FROM_CART_DIMENSION;
    case EDIT_DESIGN_FROM_CART_CONTENT_WARNING_QUERY_STRING:
      return EDIT_DESIGN_FROM_CART_CONTENT_WARNING_DIMENSION;
    default:
      return;
  }
};
